<template>
  <div class="filter-item tochituka-points-area-filter">
    <select v-model="selectedCity" class="uk-select" aria-label="Select City" :disabled="!cities.length">
      <option value="" key="0">市町村を選択</option>
      <option v-for="city in cities" :value="city" :key="city">{{ city }}</option>
    </select>
  </div>
</template>

<script setup lang="ts">
/**
 * tochituka専用の一覧ページの地域絞り込み
 * tochitukaは都道府県フィールドを持たない前提のため、
 * 都道府県セレクト・都道府県マスタ参照・エリアページへの遷移は持ち込まず、
 * 表示中の店舗（PointsService.resultPoints）から市町村フィールドのみを見て候補を作成し、
 * 選択した市町村でページ内絞り込みのみを行う
 */
import { computed, onUnmounted, ref, watch } from 'vue'
import PointsService from '@/services/PointsService.ts'

/**
 * 集約・絞り込みに使うPointの追加フィールドキー（市町村）
 */
const CITY_EXTRA_FIELD_KEY = '市町村'

/**
 * 選択中の市町村名
 */
const selectedCity = ref('')

/**
 * 市町村の選択肢
 * 表示中の店舗（都道府県による絞り込み前）の追加フィールドから市町村名を収集する
 * @returns {Array} [市町村名, ...]（50音順）
 */
const cities = computed<string[]>(() => {
  const cityNames = PointsService.resultPoints.value
    .map(p => p.extra_fields?.[CITY_EXTRA_FIELD_KEY])
    .filter((city): city is string => !!city)

  return Array.from(new Set(cityNames)).sort((a, b) => a.localeCompare(b, 'ja'))
})

/**
 * PointsService.applyFiltersToPoints の元の実装
 * 市での絞り込みを追加するため、退避してラップする
 */
const originalApplyFiltersToPoints = PointsService.applyFiltersToPoints.bind(PointsService)

/**
 * 市町での絞り込みを追加した一覧絞り込み処理
 *
 * area_filter.enabled=falseのため、共通のFilters.selectedExtraFields経路
 * （Filters.selectedExtraFields.isNeedApply）は使えず、常にfalseとなり店舗が絞り込まれない
 * そのため、市町での絞り込みはPointsService.resultPoints自体を一時的に絞り込んだ状態にして
 * 元の絞り込み（マーカー・検索条件・並び替え・ページネーション）を実行し、その後元に戻す
 */
PointsService.applyFiltersToPoints = function () {
  const allResultPoints = this.resultPoints.value

  if (selectedCity.value) {
    this.resultPoints.value = allResultPoints.filter(p => p.extra_fields?.[CITY_EXTRA_FIELD_KEY] === selectedCity.value)
  }

  originalApplyFiltersToPoints()

  this.resultPoints.value = allResultPoints
}

/**
 * setup再実行時の多重ラップを防ぐため、破棄時に元の実装へ戻す
 */
onUnmounted(() => {
  PointsService.applyFiltersToPoints = originalApplyFiltersToPoints
})

/**
 * 市町村選択時、1ページ目から絞り込み直す
 */
watch(selectedCity, () => {
  PointsService.page.value = 1
  PointsService.applyFiltersToPoints()
  document.body.scrollTo(0, 0)
})
</script>

<style lang="scss" scoped>
.tochituka-points-area-filter {
  select.uk-select {
    border: 1px solid #ddd;
    border-radius: 20px;
    font-size: 0.875rem !important;
    padding-left: 16px;
  }
}
</style>
